/* eslint-disable no-restricted-syntax */
/* eslint-disable max-classes-per-file */
import { Stream, PassThrough, Transform, TransformCallback } from 'stream';
import Mic from './MicrophoneStream';
import constants from './constants';
import MicrophoneStream from './MicrophoneStream';

type AudioEvent = {
  AudioEvent: { AudioChunk: Buffer };
};

export interface AudioService {
  startRecording(): Promise<AsyncIterable<{ AudioEvent: { AudioChunk: any } }>>;
  stop(): Promise<void>;
}

export class DualMediaStreamAudioService implements AudioService {
  private localStream?: MediaStream;
  private remoteStream?: MediaStream;
  private stream?: MediaStream;
  private micStream?: MicrophoneStream;
  private hasStopped = false;
  private audioPayloadStream?: PassThrough;
  private isReadyHandler: {
    promise: Promise<boolean>;
    resolve: (value: boolean) => void;
  } = Promise.withResolvers();

  public onStreamsUpdated: DualMediaStreamAudioService['updateStreams'] =
    this.updateStreams.bind(this);

  async isReady() {
    return this.isReadyHandler.promise;
  }

  public setMuted = (muted: boolean) => {
    this.localStream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  };

  updateStreams(localStream?: MediaStream, remoteStream?: MediaStream, partialUpdate?: boolean) {
    if (partialUpdate) {
      this.localStream = localStream ?? this.localStream;
      this.remoteStream = remoteStream ?? this.remoteStream;
    } else {
      this.localStream = localStream;
      this.remoteStream = remoteStream;
    }

    this.stream = this.getStream();
    if (!this.stream) {
      return;
    }

    this.micStream = new Mic();
    this.micStream.setStream(this.stream);

    console.log('[debug] closing audio', !!this.audioPayloadStream);
    this.audioPayloadStream?.end();

    this.audioPayloadStream = (this.micStream as unknown as Stream)
      .pipe(new EncodePcmStream(true))
      .pipe(new PassThrough({ highWaterMark: 1 * 1024 }));

    if (this.localStream && this.remoteStream) {
      this.isReadyHandler.resolve(true);
    }
  }

  async startRecording(): Promise<AsyncIterable<{ AudioEvent: { AudioChunk: any } }>> {
    this.hasStopped = false;

    const isReady = await this.isReady();
    console.log('[debug]', 'READY!');
    if (!isReady) {
      throw new Error('Error starting to record, was not ready');
    }

    const self = this;

    return {
      async *[Symbol.asyncIterator]() {
        while (!self.hasStopped) {
          const asyncIter = streamAsyncIterator(self.audioPayloadStream!);
          for await (const chunk of asyncIter) {
            if (self.hasStopped) {
              return;
            }
            if (chunk) {
              const isAllZeros = chunk?.every((byte: number) => byte === 0);
              if (!isAllZeros) {
                yield { AudioEvent: { AudioChunk: chunk } };
              }
            }
            if (self.hasStopped) break;
          }
        }
      },
    };
  }

  getStream() {
    if (!this.remoteStream || !this.localStream) {
      console.log('[DEBUG MicService] getStream() - missing stream(s)', {
        hasRemote: !!this.remoteStream,
        hasLocal: !!this.localStream,
      });
      return;
    }

    console.log('[DEBUG MicService] getStream() - streams present');
    console.log('[DEBUG MicService] localStream:', this.localStream);
    console.log('[DEBUG MicService] localStream audio tracks:', this.localStream.getAudioTracks());
    console.log(
      '[DEBUG MicService] localStream audio tracks count:',
      this.localStream.getAudioTracks().length,
    );
    console.log('[DEBUG MicService] remoteStream:', this.remoteStream);
    console.log(
      '[DEBUG MicService] remoteStream audio tracks:',
      this.remoteStream.getAudioTracks(),
    );
    console.log(
      '[DEBUG MicService] remoteStream audio tracks count:',
      this.remoteStream.getAudioTracks().length,
    );

    const context = new AudioContext({ sampleRate: constants.AUDIO_RATE });
    // const context = new AudioContext();
    const destination = context.createMediaStreamDestination();
    let remoteSource: MediaStreamAudioSourceNode;
    let localSource: MediaStreamAudioSourceNode;
    try {
      console.log('[DEBUG MicService] About to create MediaStreamSource for remoteStream');
      remoteSource = context.createMediaStreamSource(this.remoteStream);
      console.log('[DEBUG MicService] Successfully created MediaStreamSource for remoteStream');
    } catch (err: any) {
      console.error('Failed to create MediaStreamSource for', 'remoteStream', err);
      throw err;
    }
    try {
      console.log('[DEBUG MicService] About to create MediaStreamSource for localStream');
      localSource = context.createMediaStreamSource(this.localStream);
      console.log('[DEBUG MicService] Successfully created MediaStreamSource for localStream');
    } catch (err: any) {
      console.error('Failed to create MediaStreamSource for', 'localStream');
      throw err;
    }

    const merger = context.createChannelMerger(2);
    localSource.connect(merger, 0, 0);
    remoteSource.connect(merger, 0, 1);
    merger.connect(destination);

    return destination.stream;
  }

  async stop() {
    this.hasStopped = true;
    this.micStream?.stop();

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
        this.localStream?.removeTrack(track);
      });
    }

    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => {
        track.stop();
        this.remoteStream?.removeTrack(track);
      });
    }

    this.audioPayloadStream?.destroy();
    this.stream?.getTracks().forEach((it) => {
      it.stop();
      this.stream?.removeTrack(it);
    });

    this.audioPayloadStream = undefined;
    this.stream = undefined;
    this.micStream = undefined;
    this.localStream = undefined;
    this.remoteStream = undefined;
  }
}

export class WindowEventAudioService implements AudioService {
  private chunks: Buffer[] = [];
  private closed = false;
  private resolveCurrentChunk: ((result: IteratorResult<AudioEvent>) => void) | null = null;
  public saveAudio = false;
  public savedChunks: Buffer[] = [];
  public onEndPromise: any | null = null;

  async startRecording() {
    this.closed = false;
    this.chunks = [];
    this.onEndPromise = Promise.withResolvers();

    window.addEventListener('message', this.eventListener);

    const self = this;

    return {
      [Symbol.asyncIterator]() {
        return {
          next: async (): Promise<IteratorResult<AudioEvent>> => {
            if (self.chunks.length > 0) {
              const chunk = self.chunks.shift()!;
              if (self.saveAudio) {
                self.savedChunks.push(chunk);
              }
              const value = { AudioEvent: { AudioChunk: chunk } };

              return { value, done: false };
            }
            if (self.closed && self.chunks.length === 0) {
              return { value: undefined, done: true };
            }

            return new Promise<IteratorResult<AudioEvent>>((resolve) => {
              self.resolveCurrentChunk = resolve;
            });
          },
        };
      },
    };
  }

  async stop() {
    window.removeEventListener('message', this.eventListener);
  }

  eventListener = (event: MessageEvent<any>) => {
    if (event.origin !== 'https://video2.careand.ca') {
      return;
    }
    const { data } = event;
    if (!data) {
      return;
    }
    if (data.type === 'audio-close') {
      this.closed = true;
      this.onEndPromise?.resolve();
      if (this.resolveCurrentChunk) {
        this.resolveCurrentChunk({ value: undefined, done: true });
        this.resolveCurrentChunk = null;
      }
      return;
    }
    if (data.type === 'audio-chunk') {
      this.chunks.push(data.buffer);
      if (this.resolveCurrentChunk) {
        const chunk = this.chunks.shift()!;

        if (this.saveAudio) {
          this.savedChunks.push(chunk);
        }

        const value = { AudioEvent: { AudioChunk: chunk } };
        this.resolveCurrentChunk({ value, done: false });
        this.resolveCurrentChunk = null;
      }
    }
  };
}

/* eslint-disable func-names */
export class MicService implements AudioService {
  private micStream?: Mic;
  public stream?: MediaStream;
  private hasStopped = false;
  private audioPayloadStream?: PassThrough;
  public deviceId: string | undefined;
  // Bumped by every startRecording and stop: a getUserMedia that resolves
  // after being superseded (concurrent start) or after stop must release its
  // tracks immediately — otherwise the orphaned MediaStream keeps the
  // microphone captured with no field referencing it.
  private acquisitionGeneration = 0;

  async startRecording() {
    this.hasStopped = false;
    const generation = ++this.acquisitionGeneration;

    const micStream = new Mic({ numChannels: 1 });
    const stream = await window.navigator.mediaDevices.getUserMedia({
      video: false,
      audio: {
        sampleRate: constants.AUDIO_RATE,
        deviceId: this.deviceId,
      },
    });
    if (generation !== this.acquisitionGeneration) {
      // Stale acquisition (a stop or newer start won the race): release the
      // just-granted microphone and hand back an empty, already-ended stream.
      stream.getAudioTracks().forEach((it) => it.stop());
      // eslint-disable-next-line no-empty-function
      return { async *[Symbol.asyncIterator]() {} };
    }

    this.micStream = micStream;
    this.stream = stream;
    this.micStream.setStream(this.stream);

    this.audioPayloadStream = (this.micStream as unknown as Stream)
      .pipe(new EncodePcmStream())
      .pipe(new PassThrough({ highWaterMark: 3000 * 1024 }));

    const self = this;

    let hasGottenSomething = false;

    return {
      async *[Symbol.asyncIterator]() {
        const asyncIter = streamAsyncIterator(self.audioPayloadStream!);
        for await (const chunk of asyncIter) {
          if (chunk) {
            const isAllZeros = chunk?.every((byte: number) => byte === 0);
            if (hasGottenSomething || !isAllZeros) {
              hasGottenSomething = true;
              yield { AudioEvent: { AudioChunk: chunk } };
            }
          }
          if (self.hasStopped) {
            break;
          }
        }
      },
    };
  }

  async stop() {
    this.hasStopped = true;
    this.acquisitionGeneration += 1;
    this.micStream?.stop();
    this.audioPayloadStream?.destroy();
    this.stream?.getAudioTracks().forEach((it) => {
      it.stop();
      this.stream?.removeTrack(it);
    });

    this.audioPayloadStream = undefined;
    this.stream = undefined;
    this.micStream = undefined;
  }
}

// UTILITIES

export class EncodePcmStream extends Transform {
  private stereo?: boolean;

  constructor(stereo?: boolean) {
    super();
    this.stereo = stereo;
  }

  _transform(chunk: any, encoding: string, callback: TransformCallback) {
    const buffer = this.stereo ? encodePcmStereo(chunk) : encodePcm(chunk);
    this.push(buffer);
    callback();
  }
}

function encodePcmStereo(chunk: any) {
  const input = Mic.toRaw(chunk);

  const numberOfChannels = 2; // Assuming stereo (2 channels)
  const channelLength = input.length / numberOfChannels;

  let offset = 0;
  const buffer = new ArrayBuffer(input.length * 2); // Need twice as much space if keeping stereo
  const view = new DataView(buffer);

  for (let i = 0; i < channelLength; i++) {
    // Process left channel sample
    let s = Math.max(-1, Math.min(1, input[i * numberOfChannels]!));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;

    // Process right channel sample
    s = Math.max(-1, Math.min(1, input[i * numberOfChannels + 1]!));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return Buffer.from(buffer);
}

function encodePcm(chunk: any) {
  const input = Mic.toRaw(chunk);

  let offset = 0;
  const buffer = new ArrayBuffer(input.length * 2);

  const view = new DataView(buffer);
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return Buffer.from(buffer);
}

async function* fromReadable(stream: PassThrough): AsyncGenerator<any, void, unknown> {
  const chunks: any[] = [];
  let waiting: (() => void) | null = null;

  // When data comes in, push it into the queue and notify if someone is waiting.
  stream.on('data', (data) => {
    chunks.push(data);
    if (waiting) {
      waiting();
      waiting = null; // clear so that we can await a new promise later if needed.
    }
  });

  // When the stream ends, push a marker (null) and notify.
  stream.on('end', () => {
    chunks.push(null);
    if (waiting) {
      waiting();
      waiting = null;
    }
  });

  // destroy() (MicService.stop) emits 'close' without 'end' — without this the
  // iterator (and any pump for-awaiting it) would hang forever.
  stream.on('close', () => {
    chunks.push(null);
    if (waiting) {
      waiting();
      waiting = null;
    }
  });

  // Optionally, handle errors on the stream.
  stream.on('error', (err) => {
    // You might want to either break the generator or yield an error.
    // Here we'll simply throw the error on the next await.
    chunks.push(null);
    if (waiting) {
      waiting();
      waiting = null;
    }
    throw err;
  });

  while (true) {
    // If there are no chunks available, wait for the next one.
    if (chunks.length === 0) {
      await new Promise<void>((resolve) => {
        waiting = resolve;
      });
    }

    // Process all accumulated chunks.
    while (chunks.length > 0) {
      const chunk = chunks.shift();
      if (chunk === null) {
        return; // end-of-stream marker encountered; exit the loop.
      }
      yield chunk;
    }
  }
}

async function* fromReadable2(stream: PassThrough) {
  let exhausted = false;
  let chunks: (Buffer | null)[] = [];
  let hasChunks = Promise.withResolvers();

  stream.on('data', (data) => {
    chunks.push(data);
    hasChunks.resolve(true);
  });
  stream.on('end', () => {
    chunks.push(null);
    hasChunks.resolve(true);
  });

  try {
    while (true) {
      let chunk;
      if (chunks.length > 0) {
        chunk = chunks.shift();
      } else {
        await hasChunks.promise;
        chunk = chunks.shift();
        hasChunks = Promise.withResolvers();
      }

      if (chunk === null) {
        exhausted = true;
        break;
      }
      yield chunk;
    }
  } finally {
    if (!exhausted) {
      stream.destroy();
    }
  }
}

export function streamAsyncIterator(stream: PassThrough) {
  return {
    [Symbol.asyncIterator]() {
      return fromReadable(stream);
    },
  };
}

class TeeBranchQueue {
  chunks: AudioEvent[] = [];
  done = false;
  private waiter?: () => void;

  push(chunk: AudioEvent) {
    if (this.done) {
      // An ended queue must not accumulate — its consumer has already drained.
      return;
    }
    this.chunks.push(chunk);
    this.notify();
  }

  end() {
    this.done = true;
    this.notify();
  }

  private notify() {
    this.waiter?.();
    this.waiter = undefined;
  }

  wait() {
    return new Promise<void>((resolve) => {
      this.waiter = resolve;
    });
  }
}

/**
 * Fans one AudioService out to an owner consumer plus follower consumers
 * (AWS Transcribe primary + Deepgram shadow reading the same microphone).
 *
 * Ownership rule: the OWNER branch controls the source. Its startRecording
 * starts the mic (and may restart it after a stop — the AWS reconnect path
 * does stop()+startRecording() on its provider); its stop() stops the mic and
 * ends every branch queue, so when the primary dies or stops the microphone
 * is deterministically released and the shadow can never hold it hostage or
 * resurrect it. Followers only register queues: their stop() detaches them
 * without touching the source.
 */
export class TeeAudioService {
  private queues = new Set<TeeBranchQueue>();
  private pumpActive = false;
  private sourceAcquisition?: ReturnType<AudioService['startRecording']>;
  // Guards against a stale pump (hung on a destroyed stream, or finishing
  // late) ending the queues of a newer pump generation.
  private pumpGeneration = 0;

  constructor(private source: AudioService) {}

  branch(options?: { owner?: boolean }): AudioService {
    const self = this;
    const isOwner = !!options?.owner;
    let queue: TeeBranchQueue | undefined;

    return {
      async startRecording() {
        queue = new TeeBranchQueue();
        self.queues.add(queue);
        if (isOwner) {
          try {
            // Await the source acquisition so a getUserMedia failure rejects
            // the owner's startRecording — otherwise the primary provider
            // "starts" against a dead microphone and its start() resolves.
            await self.ensurePump();
          } catch (err: any) {
            queue.end();
            self.queues.delete(queue);
            queue = undefined;
            throw err;
          }
        }
        const branchQueue = queue;
        return (async function* () {
          while (true) {
            if (branchQueue.chunks.length > 0) {
              yield branchQueue.chunks.shift()!;
              continue;
            }
            if (branchQueue.done) {
              return;
            }
            await branchQueue.wait();
          }
        })();
      },
      async stop() {
        if (queue) {
          queue.end();
          self.queues.delete(queue);
          queue = undefined;
        }
        if (isOwner) {
          await self.stopSource();
        }
      },
    };
  }

  // Returns the source-acquisition promise so the owner can await it (and
  // surface getUserMedia failures); the pump itself runs unawaited.
  private ensurePump(): Promise<unknown> {
    if (this.pumpActive && this.sourceAcquisition) {
      return this.sourceAcquisition;
    }
    this.pumpActive = true;
    this.pumpGeneration += 1;
    this.sourceAcquisition = this.source.startRecording();
    this.pump(this.pumpGeneration, this.sourceAcquisition);
    return this.sourceAcquisition;
  }

  private async pump(generation: number, acquisition: ReturnType<AudioService['startRecording']>) {
    try {
      const iterable = await acquisition;
      if (generation !== this.pumpGeneration && !this.pumpActive) {
        // The session stopped while audio acquisition (e.g. the getUserMedia
        // permission prompt) was pending — stopSource had nothing to release
        // at the time, so release the just-acquired microphone here. Skipped
        // when a newer pump owns the source.
        Promise.resolve(this.source.stop()).catch((err: any) =>
          console.error('[TeeAudioService] Failed to release late-acquired audio:', err),
        );
        return;
      }
      for await (const chunk of iterable) {
        if (generation !== this.pumpGeneration) {
          // A stop+restart cycle superseded this pump.
          return;
        }
        this.queues.forEach((queue) => queue.push(chunk));
      }
    } catch (err: any) {
      console.error('[TeeAudioService] pump error:', err);
    } finally {
      if (generation === this.pumpGeneration) {
        this.endSession();
      }
    }
  }

  /**
   * Ends the audio session synchronously — the source's stop() may destroy
   * its stream in a way the pump can never observe, so branch queues are
   * closed here instead of relying on the pump's iterable completing.
   */
  private endSession() {
    this.pumpActive = false;
    this.pumpGeneration += 1;
    this.queues.forEach((queue) => queue.end());
    this.queues.clear();
  }

  private async stopSource() {
    this.endSession();
    await this.source.stop();
  }
}
